// Gesten-Fundament: reine Hilfsfunktionen ohne React.
// Prinzipien (apple-design): 1:1-Tracking, Velocity-Übergabe beim Loslassen,
// Rubber-Banding an Grenzen, jederzeit unterbrechbar (Animation startet vom
// aktuellen Wert), nur transform/opacity.

export const IOS_EASE = [0.32, 0.72, 0, 1];
export const IOS_EASE_CSS = 'cubic-bezier(0.32, 0.72, 0, 1)';

// iOS-Rubber-Band: je weiter über die Grenze, desto stärker gedämpft.
export function rubberband(overshoot, dimension, constant = 0.55) {
  if (!dimension) return overshoot * constant;
  const sign = overshoot < 0 ? -1 : 1;
  const o = Math.abs(overshoot);
  return sign * (o * dimension * constant) / (dimension + constant * o);
}

// Wert in [min,max] halten, darüber hinaus rubber-banden.
export function rubberClamp(value, min, max, dimension) {
  if (value < min) return min + rubberband(value - min, dimension);
  if (value > max) return max + rubberband(value - max, dimension);
  return value;
}

// Momentum-Projektion (Apple, Designing Fluid Interfaces). velocity in px/ms.
export function project(velocity, decelerationRate = 0.998) {
  return (velocity * decelerationRate) / (1 - decelerationRate);
}

// Geschwindigkeit aus den letzten ~100 ms Bewegung (px/ms).
export function createVelocityTracker(windowMs = 100) {
  let samples = [];
  return {
    reset() { samples = []; },
    add(value, t = performance.now()) {
      samples.push([t, value]);
      while (samples.length > 2 && t - samples[0][0] > windowMs) samples.shift();
    },
    velocity() {
      if (samples.length < 2) return 0;
      const [t0, v0] = samples[0];
      const [t1, v1] = samples[samples.length - 1];
      // Finger steht still (letzte Bewegung zu alt) → keine Restgeschwindigkeit
      if (performance.now() - t1 > 80) return 0;
      const dt = t1 - t0;
      return dt > 0 ? (v1 - v0) / dt : 0;
    },
  };
}

// Kubische Bézier-Kurve (wie CSS), Newton + Bisektion.
function bezier(x1, y1, x2, y2) {
  const cx = 3 * x1, bx = 3 * (x2 - x1) - cx, ax = 1 - cx - bx;
  const cy = 3 * y1, by = 3 * (y2 - y1) - cy, ay = 1 - cy - by;
  const sx = (t) => ((ax * t + bx) * t + cx) * t;
  const sy = (t) => ((ay * t + by) * t + cy) * t;
  const dx = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 6; i++) {
      const err = sx(t) - x;
      const d = dx(t);
      if (Math.abs(err) < 1e-5) return sy(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    let lo = 0, hi = 1; t = x;
    for (let i = 0; i < 24; i++) {
      const v = sx(t);
      if (Math.abs(v - x) < 1e-5) break;
      if (v < x) lo = t; else hi = t;
      t = (lo + hi) / 2;
    }
    return sy(t);
  };
}
const iosEase = bezier(...IOS_EASE);
// Anfangssteigung der iOS-Kurve (dy/dx bei t=0) — für Velocity-Matching
const IOS_START_SLOPE = IOS_EASE[1] / IOS_EASE[0];

// Dauer so wählen, dass die Startgeschwindigkeit der Kurve ≈ Fingergeschwindigkeit ist
// (kein Bruch zwischen Drag und Animation), begrenzt auf ruhige iOS-Werte.
export function durationFor(distance, velocity, { min = 180, max = 420, base = 340 } = {}) {
  const d = Math.abs(distance);
  if (d < 0.5) return 0;
  const v = Math.abs(velocity);
  if (v < 0.15) return Math.min(max, Math.max(min, base * Math.min(1, 0.55 + d / 600)));
  return Math.min(max, Math.max(min, (IOS_START_SLOPE * d) / v));
}

// Unterbrechbarer rAF-Tween. Gibt { stop(), current() } zurück; stop() liefert den
// aktuell sichtbaren Wert, damit eine neue Geste ohne Sprung übernehmen kann.
export function tween({ from, to, duration, onUpdate, onDone }) {
  let raf = 0;
  let value = from;
  const start = performance.now();
  if (!duration) {
    value = to;
    onUpdate(to);
    onDone?.();
    return { stop: () => value, current: () => value };
  }
  const step = (now) => {
    const p = Math.min(1, (now - start) / duration);
    value = from + (to - from) * iosEase(p);
    onUpdate(value);
    if (p < 1) raf = requestAnimationFrame(step);
    else { raf = 0; onDone?.(); }
  };
  raf = requestAnimationFrame(step);
  return {
    stop() { if (raf) cancelAnimationFrame(raf); raf = 0; return value; },
    current: () => value,
  };
}

export function prefersReducedMotion() {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
}

// Aktuelle translateX/Y eines Elements aus der berechneten Matrix (für Unterbrechung
// laufender CSS-Animationen, z.B. der Sheet-Einfahr-Animation).
export function readTranslate(el) {
  const t = getComputedStyle(el).transform;
  if (!t || t === 'none') return { x: 0, y: 0 };
  const m = t.match(/matrix(3d)?\(([^)]+)\)/);
  if (!m) return { x: 0, y: 0 };
  const v = m[2].split(',').map(Number);
  return m[1] ? { x: v[12], y: v[13] } : { x: v[4], y: v[5] };
}
